import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { Radio } from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { usePlans } from '../hooks/usePlans';
import { adminAPI } from '../services/api';
import { APP_CONFIG } from '../helpers/constants';
import { 
  getTableHeaderStyles, 
  getTableContainerStyles, 
  getTableStyles,
  getTableCellStyles,
  getTableRowStyles
} from '../styles/tableStyles';

import { formatTableDate } from '../utils/dateUtils';

interface PlanFormData {
  name: string;
  planCode: string;
  description: string;
  monthlyPrice: number;
  planCycles: number;
  features: string[];
  isActive: boolean;
  interval: number;
  intervalUnit: string;
}

const SubscriptionPlans: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { showSuccess, showError } = useToast();
  const platform_name = useMemo(() => APP_CONFIG.PLATFORM_NAME, []);
  const { plans, loading, error, refetch } = usePlans(platform_name);
  
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter] = useState('all');
  
  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [formData, setFormData] = useState<PlanFormData>({
    name: '',
    planCode: '',
    description: '',
    monthlyPrice: 0,
    planCycles: -1,
    features: [''],
    isActive: true,
    interval: 1,
    intervalUnit: 'months',
  });
  const [formErrors, setFormErrors] = useState<any>({});

  // Plan details modal state
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  // Loading state for create/update plan
  const [isCreatingOrUpdatingPlan, setIsCreatingOrUpdatingPlan] = useState(false);

  // Filter plans based on search and status
  const filteredPlans = plans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plan.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && plan.isActive) ||
                         (statusFilter === 'inactive' && !plan.isActive);
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Sort by monthly price: 0 (free) at top, highest price at bottom
    const priceA = Number(a.monthlyPrice) || 0;
    const priceB = Number(b.monthlyPrice) || 0;
    return priceA - priceB;
  });

  const handleOpenModal = (plan?: any) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        planCode: plan.planCode,
        description: plan.description,
        monthlyPrice: plan.monthlyPrice,
        planCycles: plan.planCycles || -1,
        features: plan.features && plan.features.length > 0 ? plan.features : [''],
        isActive: plan.isActive,
        interval: plan.interval || 1,
        intervalUnit: 'months',
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        planCode: '',
        description: '',
        monthlyPrice: 0,
        planCycles: -1,
        features: [''],
        isActive: true,
        interval: 1,
        intervalUnit: 'months',
      });
    }
    setFormErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPlan(null);
    setFormErrors({});
    setIsCreatingOrUpdatingPlan(false);
  };

  const handleOpenDetailsModal = (plan: any) => {
    setSelectedPlan(plan);
    setOpenDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setOpenDetailsModal(false);
    setSelectedPlan(null);
  };

  const handleEditFromDetails = () => {
    handleCloseDetailsModal();
    handleOpenModal(selectedPlan);
  };

  const handleDeleteFromDetails = async () => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await adminAPI.deletePlan(selectedPlan.id, { platform_name });
        showSuccess('Plan deleted successfully!');
        handleCloseDetailsModal();
        refetch();
      } catch (error: any) {
        console.error('Error deleting plan:', error);
        showError(error.message || 'Failed to delete plan');
      }
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: '',
      });
    }
  };

  const handleNumberChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value) || 0;
    setFormData({
      ...formData,
      [field]: value,
    });
    if (formErrors[field]) {
      setFormErrors({
        ...formErrors,
        [field]: '',
      });
    }
  };



  const handleFeaturesChange = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: newFeatures,
    });
  };

  const handleAddFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, ''],
    });
  };

  const handleRemoveFeature = (index: number) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      features: newFeatures.length > 0 ? newFeatures : [''],
    });
  };

  const handleCreateOrUpdatePlan = async () => {
    // Reset form errors
    setFormErrors({});

    // Validate required fields
    const errors: any = {};
    if (!formData.name) {
      errors.name = 'Plan name is required';
    }
    if (!formData.planCode) {
      errors.planCode = 'Plan code is required';
    }
    if (!formData.description) {
      errors.description = 'Description is required';
    }
    if (formData.monthlyPrice < 0) {
      errors.monthlyPrice = 'Price must be non-negative';
    }
    if (formData.planCycles > 0 && formData.planCycles < 1) {
      errors.planCycles = 'Plan cycles must be at least 1';
    }
    
    // Validate features
    const validFeatures = formData.features.filter(feature => feature.trim() !== '');
    if (validFeatures.length === 0) {
      errors.features = 'At least one feature is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Set loading state
    setIsCreatingOrUpdatingPlan(true);

    try {
      // Prepare data for backend
      console.log('Form data before creating planData:', formData);
      const planData = {
        name: formData.name,
        planCode: formData.planCode,
        description: formData.description,
        monthlyPrice: formData.monthlyPrice,
        planCycles: formData.planCycles,
        features: validFeatures,
        isActive: formData.isActive,
        interval: formData.interval,
        intervalUnit: formData.intervalUnit,
        platform_name: platform_name,
      };
      console.log('Plan data being sent:', planData);

      if (editingPlan) {
        await adminAPI.updatePlan(editingPlan.id, planData);
        showSuccess('Plan updated successfully!');
      } else {
        await adminAPI.createPlan(planData);
        showSuccess('Plan created successfully!');
      }
      
      handleCloseModal();
      refetch();
    } catch (error: any) {
      console.error('Error saving plan:', error);
      showError(error.message || 'Failed to save plan');
    } finally {
      // Reset loading state
      setIsCreatingOrUpdatingPlan(false);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await adminAPI.deletePlan(planId, { platform_name });
        showSuccess('Plan deleted successfully!');
        refetch();
      } catch (error: any) {
        console.error('Error deleting plan:', error);
        showError(error.message || 'Failed to delete plan');
      }
    }
  };



  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header: search left, create button right (page title in Layout toolbar) */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ 
              width: 200,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              },
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenModal()}
          size="small"
        >
          CREATE PLAN
        </Button>
      </Box>

      {/* Plans table */}
      <Paper sx={getTableContainerStyles()}>
        <TableContainer>
          <Table {...getTableStyles()}>
            <TableHead>
              <TableRow sx={getTableRowStyles()}>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Plan Name</TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Plan Cost</TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Subscribers</TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Added By</TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Last Modified</TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Status</TableCell>
                <TableCell sx={getTableHeaderStyles(isDarkMode)}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={7} align="center" sx={getTableCellStyles()}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={7} align="center" sx={getTableCellStyles()}>
                    <Alert severity="error" sx={{ my: 2 }}>
                      {error}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : filteredPlans.length === 0 ? (
                <TableRow sx={getTableRowStyles()}>
                  <TableCell colSpan={7} align="center" sx={getTableCellStyles()}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No plans found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPlans.map((plan) => (
                  <TableRow key={plan.id} sx={getTableRowStyles()}>
                    <TableCell sx={getTableCellStyles()}>
                      <Typography
                        component="span"
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'primary.dark',
                          },
                        }}
                        onClick={() => handleOpenDetailsModal(plan)}
                      >
                        {plan.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      {plan.monthlyPrice === 0 ? (
                        '-'
                      ) : (
                        <span>${(() => {
                          const price = Number(plan.monthlyPrice);
                          return Number.isInteger(price) ? price : price.toFixed(2);
                        })()}</span>
                      )}
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      {plan.subscriberCount || 0}
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      {plan.createdByEmail || '-'}
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      {formatTableDate(plan.updatedAt || plan.createdAt, 'updated')}
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Chip
                        label={plan.isActive ? 'Active' : 'Inactive'}
                        color={plan.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={getTableCellStyles()}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(plan)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePlan(plan.id)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Plan Modal */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
            </Typography>
            <IconButton 
              onClick={handleCloseModal} 
              size="small"
              sx={{ 
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Plan Information */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Plan Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Plan Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  fullWidth
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  placeholder="e.g., Trial, Starter, Professional"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Plan Code"
                  value={formData.planCode}
                  onChange={handleInputChange('planCode')}
                  fullWidth
                  required
                  error={!!formErrors.planCode}
                  helperText={formErrors.planCode}
                  placeholder="e.g., TRIAL, STARTER, PRO"
                  disabled={!!editingPlan}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  fullWidth
                  multiline
                  rows={3}
                  required
                  error={!!formErrors.description}
                  helperText={formErrors.description}
                  placeholder="Describe what this plan includes..."
                />
              </Grid>
            </Grid>

            {/* Plan Pricing */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Plan Pricing
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Plan Cost ($/month)"
                  type="number"
                  value={formData.monthlyPrice}
                  onChange={handleNumberChange('monthlyPrice')}
                  fullWidth
                  required
                  error={!!formErrors.monthlyPrice}
                  helperText={formErrors.monthlyPrice}
                  placeholder="0"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Billing Interval"
                  type="number"
                  value={formData.interval}
                  onChange={handleNumberChange('interval')}
                  fullWidth
                  required
                  error={!!formErrors.interval}
                  helperText={formErrors.interval}
                  placeholder="1"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">units</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Interval Unit"
                  value="Months"
                  fullWidth
                  disabled
                  InputProps={{
                    endAdornment: <InputAdornment position="end">months</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>

            {/* Plan Cycles */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Plan Cycles
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl component="fieldset">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Radio
                        checked={formData.planCycles === -1}
                        onChange={(_e) => {
                          setFormData({
                            ...formData,
                            planCycles: -1
                          });
                        }}
                        value="-1"
                      />
                    }
                    label="Auto-renews until cancelled"
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={formData.planCycles > 0}
                        onChange={(_e) => {
                          setFormData({
                            ...formData,
                            planCycles: 1
                          });
                        }}
                        value="1"
                      />
                    }
                    label="Expires after a specified no. of plan cycles"
                  />
                </Box>
              </FormControl>
              
              {formData.planCycles > 0 && (
                <TextField
                  label="Number of Cycles"
                  type="number"
                  value={formData.planCycles}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    setFormData({
                      ...formData,
                      planCycles: value
                    });
                  }}
                  fullWidth
                  required
                  error={!!formErrors.planCycles}
                  helperText={formErrors.planCycles}
                  placeholder="1"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">cycles</InputAdornment>,
                  }}
                />
              )}
            </Box>

            {/* Features */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Features
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {formData.features.map((feature, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label={`Feature ${index + 1}`}
                    value={feature}
                    onChange={(e) => handleFeaturesChange(index, e.target.value)}
                    fullWidth
                    placeholder="e.g., API Access, Email Support, Analytics"
                    error={!!formErrors.features && index === 0}
                    helperText={formErrors.features && index === 0 ? formErrors.features : ''}
                  />
                  {formData.features.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveFeature(index)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              
              <Button
                variant="text"
                startIcon={<Add />}
                onClick={handleAddFeature}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Feature
              </Button>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseModal}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateOrUpdatePlan}
            variant="contained"
            sx={{ textTransform: 'none' }}
            disabled={isCreatingOrUpdatingPlan}
          >
            {isCreatingOrUpdatingPlan ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              editingPlan ? 'Update Plan' : 'Create Plan'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Details Modal */}
      <Dialog
        open={openDetailsModal}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Plan Details: {selectedPlan?.name}
            </Typography>
            <IconButton 
              onClick={handleCloseDetailsModal} 
              size="small"
              sx={{ 
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxWidth: 450, mx: 'auto', py: 2 }}>
            {/* Plan Card - Clean Design */}
            <Card sx={{ 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative'
            }}>
              <CardContent sx={{ p: 0 }}>
                {/* Plan Header with Light Background */}
                <Box sx={{ 
                  background: '#f8f9fa',
                  p: 4,
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    color: '#1a1a1a',
                    mb: 1,
                    fontSize: '2rem'
                  }}>
                    {selectedPlan?.name}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    color: '#6c757d',
                    mb: 3,
                    fontSize: '1rem',
                    lineHeight: 1.5
                  }}>
                    {selectedPlan?.description || 'Subscription Plan'}
                  </Typography>
                  
                  {/* Left-aligned Price Section */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'baseline'
                  }}>
                    <Typography variant="h2" sx={{ 
                      fontWeight: 700,
                      color: '#1a1a1a',
                      mr: 1,
                      fontSize: '2.5rem'
                    }}>
                      {selectedPlan?.monthlyPrice === 0 ? 'Free' : `$${selectedPlan?.monthlyPrice}`}
                    </Typography>
                    {selectedPlan?.monthlyPrice !== 0 && (
                      <Typography variant="body1" sx={{ 
                        color: '#6c757d',
                        fontWeight: 400
                      }}>
                        / month
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Features Section */}
                <Box sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ 
                    mb: 3, 
                    fontWeight: 600,
                    color: '#1a1a1a'
                  }}>
                    Features
                  </Typography>
                  
                  {selectedPlan?.features && selectedPlan.features.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {selectedPlan.features.map((feature: string, index: number) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          alignItems: 'center'
                        }}>
                          <CheckCircle sx={{ 
                            mr: 2, 
                            color: '#2e7d32', 
                            fontSize: 20,
                            flexShrink: 0
                          }} />
                          <Typography variant="body1" sx={{ 
                            color: '#1a1a1a',
                            fontWeight: 400,
                            fontSize: '1rem'
                          }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      py: 1
                    }}>
                      <Cancel sx={{ 
                        mr: 2, 
                        color: '#6c757d', 
                        fontSize: 20 
                      }} />
                      <Typography variant="body1" sx={{ 
                        color: '#6c757d',
                        fontWeight: 400
                      }}>
                        No features defined for this plan.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditFromDetails} variant="outlined" sx={{ textTransform: 'none' }}>
            Edit Plan
          </Button>
          <Button onClick={handleDeleteFromDetails} variant="contained" color="error" sx={{ textTransform: 'none' }}>
            Delete Plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlans; 